import { Fragment, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { ChartTooltip } from '../../molecules/ChartTooltip/ChartTooltip';
import { token } from '../../tokens/tokens';
import styles from './CalendarHeatmap.module.css';

/**
 * A heat grid: one cell per (row, column) pair, shaded by how much landed in it.
 *
 * **Ported from spectrum-ui's `CalendarHeatmap`**, kept unmodified at
 * `reference/spectrum-ui/charts/calendar-heatmap.tsx`, and carrying the grid
 * logic of the DeepDive v4 prototype rather than spectrum's own. The two
 * differ in what the axes are, and that is the whole of the difference:
 *
 *   - spectrum-ui draws a **contribution graph** — 53 week columns against 7
 *     weekday rows, a year of days, each cell a date;
 *   - the prototype draws an **activity grid** — 24 hour columns against 7
 *     weekday rows, where a cell is a recurring *time of week* and the same
 *     Tuesday 7pm from eight different weeks folds into one square.
 *
 * This component is the second, generalised: it takes row labels, column labels
 * and a matrix, and knows nothing about dates. `data/grid.ts` builds the matrix.
 *
 * What came from spectrum-ui: the component's shape, the intensity bucketing,
 * the `Less … More` scale, the single hover readout, the empty state, and the
 * decision to draw the grid as marks with axis labels outside it rather than as
 * a table. What did not: the SVG substrate and its measured viewBox (see the
 * stylesheet), the staggered entrance (see the stylesheet), the
 * `usePrefersReducedMotion` hook — motion.css collapses durations at the token
 * layer, so nothing here needs to ask — and `intensityColor`'s `color-mix`,
 * which would mint a colour the file does not define.
 *
 * **Not in Figma**, like `ScatterChart` before it. The file draws cartesian
 * chrome and stops; there is no heat grid, no intensity ramp and no cell in it.
 * Every value resolves to a token all the same — see the stylesheet, where each
 * one says what it was derived from.
 *
 * It does not use `ChartFrame`, which is the one piece of chrome it cannot
 * borrow: that component wraps its children in Recharts' `ResponsiveContainer`
 * to measure them, and this grid is sized by CSS Grid instead. It mirrors the
 * frame's own structure — plot box, `role="img"`, the same two replaced states,
 * a legend slot underneath — rather than inheriting it.
 *
 * The grid is laid out at its natural size and left-aligned rather than
 * stretched to fill its slot — see the stylesheet for why a heat grid wants a
 * small, regular, square mark.
 *
 * ```tsx
 * <CalendarHeatmap rowLabels={WEEKDAYS} colLabels={HOURS.map(hourLabel)} values={grid.values} />
 * ```
 */
export type CalendarHeatmapProps = {
  /** One label per row, top to bottom. */
  rowLabels: readonly string[];
  /** One label per column, left to right. */
  colLabels: readonly string[];
  /** `values[row][col]`, the same shape as `rowLabels` x `colLabels`. */
  values: readonly (readonly number[])[];
  /**
   * Plot height in px, excluding the scale. Defaults to Figma's `Chart` slot.
   * `'fill'` takes the height of the slot the grid is in instead.
   */
  height?: number | 'fill';
  /**
   * The side of one square cell, in px.
   *
   * A number rather than a token, for the reason every other dimension in this
   * system is one: Figma binds no variable to an element size. It lives here
   * rather than in the stylesheet because the cell and the column-label width
   * have to agree, and one number passed in cannot drift where two written out
   * can. spectrum-ui's original carries the same prop, at 13.
   *
   * 18 is sized for the card Figma actually draws this in — a 586 half-card,
   * whose chart slot is 554. 24 columns at 18 on a `space/2` gap is 478px plus
   * the weekday gutter, which still fits; at 24 it would not.
   */
  cell?: number;
  /**
   * Draw every nth column label, counting from the first.
   *
   * Defaults to whatever keeps the count near ten, which is this system's own
   * axis rule — "time-axis labels thin by count, not by pixel width". 24 hours
   * therefore label every third, and the seven weekday rows are all labelled
   * because seven is already under the target.
   */
  colLabelInterval?: number;
  /** Formats a cell's value in the readout. */
  formatValue?: (value: number) => string;
  /**
   * What one unit is — `Engagement`. Names the value in the readout, where a
   * bare number beside a weekday and an hour says nothing about what was
   * counted.
   */
  unit?: string;
  /** Draw the intensity scale below the grid. `none` when a `Card` slot holds it. */
  legend?: 'bottom' | 'none';
  /** Replaces the grid with a loading message. */
  loading?: boolean;
  /** Replaces the grid with an empty message. Worked out from the data if unset. */
  empty?: boolean;
  emptyLabel?: ReactNode;
  loadingLabel?: ReactNode;
  /** Accessible name. Derived from the axes when omitted. */
  label?: string;
  className?: string;
  /**
   * Opens the detail behind one cell — the conversations that hour of that day
   * stands on.
   *
   * **No dwell-to-lock here**, unlike the Recharts charts. This readout has
   * always been anchored: it is a child of the cell it describes, positioned
   * against that cell's own box, and it has never moved while the pointer stays
   * inside one. There is nothing to freeze, so the action is live from the first
   * frame — see `useAnchoredTooltip` for what the other charts had to do to
   * reach the same place.
   */
  onViewDetails?: (row: number, col: number) => void;
  /** What the readout's action reads. */
  viewDetailsLabel?: ReactNode;
};

/**
 * Intensity steps above the track. Five, which is the prototype's own count —
 * spectrum-ui uses four.
 *
 * Five is what the stylesheet has equal fifths of, so the two cannot disagree:
 * a sixth step here would paint `data-step='6'`, which no rule matches, and the
 * cell would fall back to full-strength `chart/1`.
 */
export const HEAT_STEPS = 5;

/**
 * How much room over a cell the readout needs before it will sit there.
 *
 * The bubble measures 70 with one series and sits `space/8` clear of the cell,
 * so 78 is what it wants; the extra is slack for a longer heading wrapping to a
 * second line. Below this the bubble flips underneath — see `flip`.
 */
const TIP_CLEARANCE = 88;

/**
 * What the busiest cell is called, in the readout and in the accessible name.
 *
 * One constant for both so they cannot drift into two different words for the
 * same mark. Deliberately the plain fact — it is the highest value on the grid —
 * rather than a domain reading like "peak window", which is the *card's* wording
 * and would be wrong the moment this component plots something other than
 * activity.
 */
const HIGHEST = 'Highest';

/**
 * Where one step ends and the next begins.
 *
 * **Quantiles of the observed values, not fractions of the maximum** — and this
 * is the one place the port deliberately corrects both sources. spectrum-ui and
 * the prototype both bucket at `ceil(value / max * steps)`, which assumes the
 * values are spread evenly up to the peak. Engagement is log-normal and is not:
 * measured on this corpus's own eight-week activity window, the busiest cell
 * holds 5,051 against a median live cell of 137, a ratio of 37 to 1. Under a
 * max-relative scale every cell but a handful falls in the first step and the
 * grid reads as one bright square on a flat field — arithmetically correct, and
 * exactly the kind of unreadable chart this repo has been caught by before.
 *
 * Ranking instead asks "how does this cell compare with the others", which is
 * the question a heat grid is read for. One viral post then colours its own cell
 * and stops there.
 *
 * Empty cells are excluded from the ranking. They are a quarter to a third of
 * this grid — the overnight hours — and folding them in would push every
 * boundary down and promote a quiet hour to a warm one.
 */
function quantileSteps(values: readonly (readonly number[])[]): number[] {
  const live = values
    .flat()
    .filter((value) => value > 0)
    .sort((a, b) => a - b);

  if (live.length === 0) return [];

  return Array.from({ length: HEAT_STEPS - 1 }, (_, i) => {
    const at = Math.floor(((i + 1) / HEAT_STEPS) * live.length);
    return live[Math.min(at, live.length - 1)];
  });
}

/**
 * The busiest cell, or `null` when nothing landed anywhere.
 *
 * **Row-major, strictly greater, so the first cell wins a tie** — which is the
 * same rule `weekHourGrid` applies in the data layer. The two have to agree:
 * that function's `peak` is what the card's `Peak window` figure names, and this
 * one is what gets the glow, so a different tie-break would light up a cell the
 * KPI is not talking about. Deliberately computed here from the values actually
 * drawn rather than taken as a prop, so the mark cannot be marking something the
 * grid does not show.
 */
function peakCell(values: readonly (readonly number[])[]): { row: number; col: number } | null {
  /*
   * Seeded at `value: 0` rather than at `null`, exactly as `weekHourGrid` does:
   * no cell can beat zero, so a grid where nothing was measured falls out as
   * "no peak" without a special case — and the shape matching the data layer's
   * is what makes the two tie-breaks legibly the same.
   */
  let best = { row: 0, col: 0, value: 0 };
  for (let row = 0; row < values.length; row += 1) {
    const cols = values[row];
    for (let col = 0; col < cols.length; col += 1) {
      if (cols[col] > best.value) best = { row, col, value: cols[col] };
    }
  }
  return best.value > 0 ? { row: best.row, col: best.col } : null;
}

/** `0` for a cell nothing landed in, then `1`…`HEAT_STEPS` up the scale. */
function stepFor(value: number, steps: readonly number[]): number {
  if (value <= 0) return 0;
  let step = 1;
  for (const boundary of steps) if (value > boundary) step += 1;
  return Math.min(step, HEAT_STEPS);
}

/**
 * The `Less … More` intensity scale.
 *
 * Exported so a `Card` can put it in its own `Legend` slot, which is where every
 * other chart in this app puts its key — `CalendarHeatmap` renders it inline
 * only when asked to stand on its own.
 *
 * The track swatch is included and sits under "Less", which is spectrum-ui's and
 * the prototype's shared choice: the reader needs to know what an *empty* cell
 * looks like, and on this grid that is a third of them.
 */
export function HeatScale({ className }: { className?: string }) {
  return (
    <div className={[styles.scale, className].filter(Boolean).join(' ')}>
      Less
      {Array.from({ length: HEAT_STEPS + 1 }, (_, step) => (
        <span
          key={step}
          className={[styles.swatch, styles.step].join(' ')}
          data-step={step}
          aria-hidden="true"
        >
          <span className={styles.fill} />
        </span>
      ))}
      More
    </div>
  );
}

export function CalendarHeatmap({
  rowLabels,
  colLabels,
  values,
  height = 224,
  cell = 18,
  colLabelInterval,
  formatValue = (value) => String(Math.round(value)),
  unit = 'Value',
  legend = 'bottom',
  loading = false,
  empty,
  emptyLabel = 'No data',
  loadingLabel = 'Loading chart…',
  label,
  onViewDetails,
  viewDetailsLabel,
  className,
}: CalendarHeatmapProps) {
  const [hovered, setHovered] = useState<{ row: number; col: number } | null>(null);

  /*
   * The readout hangs **above** its cell, and the grid clips it.
   *
   * `overflow-x: auto` is on the grid as a floor for a slot narrower than the
   * marks — see the rule. CSS will not let one axis scroll while the other
   * spills: `overflow-y: visible` computes to `auto` beside it, and
   * `overflow-y: clip` (which `overflow-clip-margin` would have let spill)
   * computes to `hidden`. Measured in the browser 2026-09-24: the top row's
   * bubble lost 18 of its 70px to that clip.
   *
   * So the bubble flips under the cell when there is not room over it — the
   * vertical half of the collision handling `data-align` already does across.
   * Measured on enter rather than derived: the room above a row is the hour
   * labels plus `row x (cell + gap)`, and `cell` is a caller's number while the
   * label row is however tall its text renders. One `getBoundingClientRect` on
   * the pointer entering a cell is cheaper than being wrong at another size.
   */
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [flip, setFlip] = useState(false);

  const steps = useMemo(() => quantileSteps(values), [values]);
  const peak = useMemo(() => peakCell(values), [values]);

  const fill = height === 'fill';

  /* ~10 labels, the target `xAxisProps` thins a dense time axis to. */
  const labelEvery = colLabelInterval ?? Math.max(1, Math.ceil(colLabels.length / 10));

  /*
   * A grid of nothing but zeroes is empty, and that is not the same test a
   * cartesian chart uses. `isEmpty` treats `0` as a reading, which on a line is
   * right — a Tuesday the filter emptied still has to be drawn. Here every cell
   * would be drawn in the track colour and the result is a blank grid claiming
   * to be a measurement, so this follows `isEmptyTotal`'s reasoning instead.
   */
  const nothing = steps.length === 0;
  const state = loading ? 'loading' : (empty ?? nothing) ? 'empty' : 'grid';

  /*
   * The glow is information, not decoration, so it is said out loud too — a
   * screen reader gets the busiest cell by name rather than a grid that merely
   * has one highlighted somewhere.
   */
  const busiestLabel = peak ? ` ${HIGHEST}: ${rowLabels[peak.row]} ${colLabels[peak.col]}.` : '';

  const name =
    label ??
    `${unit} by ${rowLabels.length === 7 ? 'weekday' : 'row'} and ${
      colLabels.length === 24 ? 'hour of day' : 'column'
    }.${busiestLabel}`;

  return (
    <div className={[styles.root, className].filter(Boolean).join(' ')}>
      <div
        className={[styles.plot, fill && styles.fillPlot].filter(Boolean).join(' ')}
        style={fill ? undefined : { height }}
        role="img"
        aria-label={name}
        aria-busy={loading || undefined}
      >
        {state === 'grid' ? (
          <div
            ref={gridRef}
            className={styles.grid}
            /*
             * Counts, not lengths — the browser derives every size from them.
             * They arrive as custom properties for `ChartFrame`'s reason: only
             * this component knows them, and the alternative is a stylesheet
             * rule per grid shape a caller might pass.
             */
            style={
              {
                '--heat-cols': colLabels.length,
                '--heat-rows': rowLabels.length,
                '--heat-cell': `${cell}px`,
              } as CSSProperties
            }
            onPointerLeave={() => setHovered(null)}
          >
            {/* The corner the two axes meet in, which carries no label. */}
            <span />
            {colLabels.map((colLabel, col) => (
              /*
               * Every column keeps its header cell even when it carries no text,
               * so the labels stay in the same tracks as the cells under them. An
               * omitted cell would shift every later column by one.
               */
              <span key={col} className={styles.colLabel}>
                {col % labelEvery === 0 ? colLabel : ''}
              </span>
            ))}

            {rowLabels.map((rowLabel, row) => (
              <Fragment key={row}>
                <span className={styles.rowLabel}>{rowLabel}</span>
                {colLabels.map((colLabel, col) => {
                  const value = values[row]?.[col] ?? 0;
                  const on = hovered?.row === row && hovered.col === col;
                  const busiest = peak?.row === row && peak.col === col;

                  return (
                    <div
                      key={col}
                      className={[styles.cell, styles.step].join(' ')}
                      data-step={stepFor(value, steps)}
                      data-hovered={on || undefined}
                      onPointerEnter={(event) => {
                        setHovered({ row, col });
                        const grid = gridRef.current;
                        if (!grid) return;
                        const room =
                          event.currentTarget.getBoundingClientRect().top -
                          grid.getBoundingClientRect().top;
                        setFlip(room < TIP_CLEARANCE);
                      }}
                    >
                      <span className={styles.fill} />
                      {on && (
                        <div
                          className={styles.tip}
                          /* Takes the pointer only when there is something in it to click. */
                          data-actionable={onViewDetails !== undefined || undefined}
                          /* Only the two outermost columns can overhang the card. */
                          data-align={
                            col === 0 ? 'start' : col === colLabels.length - 1 ? 'end' : undefined
                          }
                          /* Under the cell when the grid would cut it off above. */
                          data-flip={flip || undefined}
                        >
                          <ChartTooltip
                            active
                            action={onViewDetails && (() => onViewDetails(row, col))}
                            actionLabel={viewDetailsLabel}
                            locked={onViewDetails !== undefined}
                            /*
                             * The glowing cell says so in words too. A reader who
                             * hovers it should not have to infer from a halo that
                             * this is the one the figure above is about — and a
                             * reader who hovers a *neighbour* needs to be able to
                             * tell they have missed it.
                             */
                            label={
                              busiest
                                ? `${rowLabel} ${colLabel} · ${HIGHEST}`
                                : `${rowLabel} ${colLabel}`
                            }
                            payload={[
                              { name: unit, value: formatValue(value), color: token['chart-1'] },
                            ]}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </Fragment>
            ))}
          </div>
        ) : (
          <p className={styles.message}>{state === 'loading' ? loadingLabel : emptyLabel}</p>
        )}
      </div>

      {legend === 'bottom' && state === 'grid' && <HeatScale />}
    </div>
  );
}
